import {
  jobApplicationService,
  TailoredResumeArtifactMetadataEntry,
  TailoredResumeArtifactType,
  TailoredResumeResponse,
} from "../services/jobApplicationService";

const DEFAULT_ARTIFACT_OPTIONS = {
  preserve_clickable_links: false,
  remove_hyperlinks_if_not_requested: true,
  return_format: "metadata_only",
};

const triggerSignedUrlDownload = (
  downloadUrl: string,
  pendingWindow: Window | null,
) => {
  if (pendingWindow && !pendingWindow.closed) {
    try {
      pendingWindow.location.replace(downloadUrl);
      return;
    } catch (_error) {
      pendingWindow.close();
    }
  }

  const link = document.createElement("a");
  link.href = downloadUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const getArtifactMetadata = (
  response: TailoredResumeResponse | null,
  artifactType: TailoredResumeArtifactType,
): TailoredResumeArtifactMetadataEntry | undefined => {
  if (!response) return undefined;
  return (
    response.artifactMetadata?.[artifactType] ||
    response.pipelineData?.artifact_metadata?.[artifactType]
  );
};

const buildArtifactFailureMessage = (
  artifactType: TailoredResumeArtifactType,
  metadata?: TailoredResumeArtifactMetadataEntry,
): string => {
  const limitations = metadata?.sanitization?.limitations?.filter(Boolean) || [];
  if (limitations.length > 0) {
    return `${artifactType.toUpperCase()} export failed: ${limitations.join(" ")}`;
  }
  return `${artifactType.toUpperCase()} export failed. Please try again.`;
};

export const downloadTailoredResumeArtifact = async (
  jobApplicationId: string,
  artifactType: TailoredResumeArtifactType,
): Promise<string> => {
  const pendingWindow = window.open("", "_blank");

  if (pendingWindow && !pendingWindow.closed) {
    try {
      pendingWindow.document.title = "Preparing download...";
      pendingWindow.document.body.innerHTML =
        "<p style=\"font-family: sans-serif; padding: 24px;\">Preparing your download...</p>";
      pendingWindow.opener = null;
    } catch (_error) {
      // Ignore document access issues and fall back to navigation once the URL is ready.
    }
  }
  try {
    const artifactGenerationResponse =
      await jobApplicationService.generateTailoredResumeArtifacts(jobApplicationId, {
      formats: [artifactType],
      outputFiles: {
        ...DEFAULT_ARTIFACT_OPTIONS,
        generate_pdf: artifactType === "pdf",
        generate_docx: artifactType === "docx",
      },
      linkHandling: {
        preserveClickableLinks: false,
        removeHyperlinks: true,
      },
    });
    const artifactMetadata = getArtifactMetadata(
      artifactGenerationResponse,
      artifactType,
    );

    if (artifactMetadata?.status === "failed") {
      throw new Error(buildArtifactFailureMessage(artifactType, artifactMetadata));
    }

    const response =
      await jobApplicationService.getTailoredResumeArtifactDownloadUrl(
        jobApplicationId,
        artifactType,
      );
    const downloadUrl =
      response.downloadUrl ||
      response.download_url ||
      response.data?.downloadUrl ||
      response.data?.download_url;

    if (!downloadUrl) {
      throw new Error(
        buildArtifactFailureMessage(artifactType, artifactMetadata),
      );
    }

    triggerSignedUrlDownload(downloadUrl, pendingWindow);
    return downloadUrl;
  } catch (error) {
    pendingWindow?.close();
    throw error;
  }
};
